<script setup>
import { useForm } from "@/form";
import AppLink from "@Components/AppLink.vue";
import { PrimaryButton } from "@Components/Button";
import { MagicIcon, WordPressIcon } from "@Icons/index";
import { UserIcon } from "lucide-vue-next";
import { computed, h, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t: __ } = useI18n();

const loaded = ref(false);
const personas = ref([]);

const formattedPersonas = computed(() => {
    return personas.value.map((p) => {
        return {
            id: p.id,
            icon: h("img", { src: p.user_avatar_url, class: "rounded-full" }),
            user_avatar_url: p.user_avatar_url,
            name: p.user_full_name,
            age: p.age,
            job: p.job,
            author_posts_url: p.author_posts_url,
        };
    });
});

onMounted(async () => {
    personas.value = await useForm({ limit: 8 }).post("personas_latest");
    loaded.value = true;
});
</script>

<template>
    <div>
        <div class="flex justify-between items-center">
            <div class="space-y-1">
                <h2 class="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">{{ __("Mes profils récents") }}</h2>
            </div>
        </div>

        <div class="mt-6">
            <div v-if="formattedPersonas.length > 0" class="grid gap-3 2xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2">
                <AppLink
                    :to="{ name: 'personas.edit', params: { id: persona.id } }"
                    v-for="persona in formattedPersonas"
                    :class="[
                        'rounded-lg overflow-hidden group',
                        'ring-inset focus:ring-2 focus:ring-inset',
                        'bg-gray-50 ring-gray-300 hover:bg-gray-100 focus:ring-gray-950',
                        'dark:bg-gray-900 dark:ring-gray-800 dark:hover:bg-gray-800 dark:focus:ring-gray-700',
                    ]">
                    <div class="flex items-center px-6 py-4 space-x-6">
                        <component v-if="persona.icon" :is="persona.icon" class="size-128 flex-none" />

                        <div>
                            <div class="flex-1">
                                <div class="text-sm font-medium text-gray-900 dark:text-white">
                                    {{ persona.name }}
                                </div>
                                <div v-if="persona.age" class="mt-1 text-sm text-gray-600 dark:text-gray-400">{{ persona.age }} {{ __("ans") }}</div>
                                <div class="flex items-center mt-1 text-sm text-gray-600 dark:text-gray-400">
                                    {{ persona.job }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="grid grid-cols-2">
                        <AppLink
                            @click.stop
                            :to="{ name: 'personas.edit', params: { id: persona.id } }"
                            class="flex justify-center items-center gap-2 p-2 dark:hover:bg-gray-700 hover:bg-gray-200">
                            <MagicIcon class="w-4 h-4" />
                            {{ __("Modifier le profil") }}
                        </AppLink>
                        <a
                            v-if="persona.author_posts_url"
                            @click.stop
                            :href="persona.author_posts_url"
                            class="flex justify-center items-center gap-2 p-2 dark:hover:bg-gray-700 hover:bg-gray-200">
                            <WordPressIcon class="w-4 h-4" />
                            {{ __("Voir les articles") }}
                        </a>
                    </div>
                </AppLink>
            </div>
            <AppLink
                v-else-if="loaded"
                :to="{ name: 'personas.create' }"
                type="button"
                class="w-full relative block p-8 text-center rounded-lg border-2 border-gray-300 border-dashed group dark:border-gray-700 dark:hover:border-white focus:border-gray-900 focus:outline-none hover:border-gray-900">
                <UserIcon class="mx-auto w-10 h-10 text-gray-600 dark:text-gray-400" />
                <div class="mt-6 text-lg font-semibold text-gray-900 dark:text-white">{{ __("Aucun profil récent") }}</div>
                <div class="mt-3 text-sm text-gray-600 dark:text-gray-400">{{ __("Vous n'avez pas encore créé de profil.") }}</div>
                <div class="flex justify-center mt-6">
                    <PrimaryButton :icon="MagicIcon">
                        {{ __("Générer un profil IA") }}
                    </PrimaryButton>
                </div>
            </AppLink>
        </div>
        <div v-if="personas.length >= 8" class="flex justify-end mt-3">
            <AppLink
                :to="{ name: 'personas.index' }"
                class="text-sm text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 hover:text-gray-700 hover:underline">
                {{ __("Voir tous les profils") }}
            </AppLink>
        </div>
    </div>
</template>
